import { Add, Close } from '@mui/icons-material';
import {
  PositionType,
  type TopInterestProfileEdit,
  v1PeopleProfileEditsCreate,
} from '@on3/api';
import { PersonAutoComplete } from '@on3/ui-lib/components/AutoComplete/PersonAutoComplete';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Checkbox } from '@on3/ui-lib/components/Checkbox/Checkbox';
import { Collapsible } from '@on3/ui-lib/components/Collapsible';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { externalApi, useToast } from '@on3/ui-lib/index';
import { isDiff } from '@on3/ui-lib/utils/compare';
import { generateKey } from '@on3/ui-lib/utils/generateKey';
import { updatePath } from '@on3/ui-lib/utils/updatePath';
import { yup } from '@on3/ui-lib/utils/yup';
import {
  type TopInterest,
  type TopInterestVisit,
} from '@public/components/Elite/Profile/Edits/TopInterests';
import { usePlayerProfile } from '@public/contexts/PlayerProfile/PlayerProvider';
import { createTitle } from '@public/utils/meta';
import { useRef, useState } from 'react';

import styles from './TeamVisitsModal.module.scss';

interface TeamVisitsModalProps {
  interest: TopInterest;
  onComplete: () => void;
}

const schema = yup.array().of(
  yup.object({
    visitKey: yup
      .mixed()
      .transform((v) => Number(v) || null)
      .nullable(),
    dateOccurred: yup.string().required().label('Date'),
    type: yup.string().oneOf(['visit', 'coaching']).required().label('Type'),
    official: yup.boolean().when('type', {
      is: 'visit',
      then: yup.boolean().required(),
    }),
    coaches: yup
      .array()
      .of(
        yup.object({
          key: yup.number().required(),
          label: yup.string().required(),
        }),
      )
      .nullable()
      .when('type', {
        is: 'coaching',
        then: yup.array().min(1, 'At least one coach is required').required(),
      }),
  }),
);

const formatTeamVisits = (visits: TopInterestVisit[]) =>
  visits.map((visit) => ({
    visitKey: visit.visitKey,
    type: visit.type,
    dateOccurred: visit.dateOccurred,
    official: visit.official,
    coaches: visit.coaches?.map((c) => ({ key: c.key, label: c.label })),
  })) as TopInterestVisit[];

export const TeamVisitsModal = ({
  interest: { $type, editType, visits, ...interest },
  onComplete = () => {},
}: TeamVisitsModalProps) => {
  const {
    state: { player },
  } = usePlayerProfile();
  const { notify } = useToast();

  const [values, setValues] = useState<TopInterestVisit[]>(
    formatTeamVisits(visits ?? []),
  );
  const prevValues = useRef<TopInterestVisit[]>(values);

  const handleAddVisit = (type: 'visit' | 'coaching') =>
    setValues((prev) => [
      {
        visitKey: generateKey() as any,
        type,
        dateOccurred: new Date().toISOString(),
        official: false,
      },
      ...prev,
    ]);

  const handleRemoveVisit = (index: number) =>
    setValues((prev) => prev.filter((_, i) => i !== index));

  const handleUpdateVisit = (
    index: number,
    key: keyof TopInterestVisit,
    value: any,
  ) => setValues((prev) => updatePath(prev, `${index}.${key}`, value));

  const handleSubmit = async (v: TopInterestVisit[]) => {
    if (!interest.recruitmentInterestKey) {
      notify(
        'Unable to update due to stale data. Please refresh and try again.',
        {
          type: 'error',
        },
      );
    }

    try {
      const updatedInterest: TopInterestProfileEdit = {
        $type,
        editType,
        ...interest,
        visits: v.map((visit) => ({
          visitKey: visit.visitKey,
          coachPersonKeys: visit.coaches?.map((c) => c.key),
          dateOccurred: visit.dateOccurred,
          type: visit.type,
          official: visit.official,
        })),
      };

      await v1PeopleProfileEditsCreate(externalApi, player.key, {
        edit: updatedInterest,
      });

      notify('Team visits updated successfully.', { type: 'success' });

      // TODO: Update this to use the new API schema
      onComplete();
    } catch (error) {
      notify('Failed to update team visits.', { type: 'error' });
    }
  };

  return (
    <Form
      className={styles.block}
      onSubmit={handleSubmit}
      schema={schema}
      values={values}
    >
      {({ processing, errors }) => (
        <>
          <header className={styles.header}>
            <h2>Update Team Visits</h2>
            <div className={styles.team}>
              <figure className={styles.avatar}>
                <Avatar
                  altText={interest.organizationName}
                  height={20}
                  source={interest.organizationAssetUrl}
                  teamAvatar
                  width={20}
                />
              </figure>
              <span className={styles.name}>{interest.organizationName}</span>
            </div>
            <div className={styles.actions}>
              <button
                className={styles.addBtn}
                disabled={processing}
                name="add-visit"
                onClick={() => handleAddVisit('visit')}
                type="button"
              >
                <Add />
                Add Visit
              </button>
              <button
                className={styles.addCoachBtn}
                disabled={processing}
                name="add-coach-visit"
                onClick={() => handleAddVisit('coaching')}
                type="button"
              >
                <Add />
                Add Coach Visit
              </button>
            </div>
          </header>
          <div className={styles.visits}>
            {values.length > 0 ? (
              <ol className={styles.visitList}>
                {values.map((visit, i) => (
                  <div className={styles.visitWrapper} key={visit.visitKey}>
                    <Collapsible
                      className={styles.visit}
                      expanded={typeof visit.visitKey === 'string'}
                      toggleContent={
                        <div className={styles.visitHeader}>
                          <span>
                            {visit.type === 'visit'
                              ? 'Visit'
                              : 'Coaching Visit'}
                          </span>
                          <div className={styles.meta}>
                            {visit.dateOccurred && (
                              <span>{visit.dateOccurred?.split('T')[0]}</span>
                            )}
                            {visit.official && <span>Official</span>}
                          </div>
                        </div>
                      }
                    >
                      <li className={styles.visitInner}>
                        <div className={styles.date}>
                          <Input
                            defaultValue={
                              visit.dateOccurred?.split('T')[0] ?? ''
                            }
                            error={errors[`[${i}].dateOccurred`]}
                            label="Date"
                            onChange={(e) =>
                              handleUpdateVisit(
                                i,
                                'dateOccurred',
                                e.target.value
                                  ? new Date(e.target.value)
                                      .toISOString()
                                      .split('T')[0]
                                  : '',
                              )
                            }
                            type="date"
                          />
                        </div>
                        <div className={styles.type}>
                          {visit.type === 'visit' ? (
                            <Checkbox
                              error={errors[`[${i}].official`]}
                              id={`visit-${visit.dateOccurred}-${i}`}
                              label="Official"
                              onChange={(val) =>
                                handleUpdateVisit(i, 'official', val)
                              }
                              value={visit.official}
                            />
                          ) : (
                            <PersonAutoComplete
                              error={errors[`[${i}].coaches`]}
                              id={`visits-${i}-coaches`}
                              label="Recruited By"
                              limit={2}
                              name={`visits-${i}-coaches`}
                              onChange={(val) =>
                                handleUpdateVisit(i, 'coaches', val)
                              }
                              optionTemplate={(c) => ({
                                key: c.key,
                                label: createTitle(
                                  c.firstName,
                                  c.lastName,
                                  c.currentOrganization?.name &&
                                    `(${c.currentOrganization?.name})`,
                                ),
                                selectedLabel: createTitle(
                                  c.firstName,
                                  c.lastName,
                                ),
                              })}
                              searchParams={{
                                positionTypes: [PositionType.Coach],
                                currentOrganizationKey:
                                  interest.organizationKey,
                              }}
                              value={(visit.coaches ?? []).filter(
                                (c) => !!c.key,
                              )}
                            />
                          )}
                        </div>
                      </li>
                    </Collapsible>
                    {typeof visit.visitKey === 'string' && (
                      <div className={styles.remove}>
                        <button
                          className={styles.removeBtn}
                          name={`remove-visit-${i}`}
                          onClick={() => handleRemoveVisit(i)}
                          type="button"
                        >
                          <span className="sr-only">Remove</span>
                          <Close />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </ol>
            ) : (
              <div className={styles.noVisits}>
                <p>
                  No visits added yet. Click the buttons above to add a visit or
                  coaching visit.
                </p>
              </div>
            )}
          </div>
          <button
            className={styles.saveBtn}
            disabled={processing || !isDiff(values, prevValues.current)}
            type="submit"
          >
            Save
          </button>
        </>
      )}
    </Form>
  );
};
